import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../../shared/api';
import { Modal, FormField } from '../../ui/Modal';
import {
  GroupServiceTypes,
  PlacesOfService,
  PlaceLabels,
  ServiceLabels,
  type DayOfWeek,
  type PlaceOfService,
} from '@census/shared';

interface GroupFormState {
  serviceType: string;
  displayName: string;
  days: DayOfWeek[];
  startMin: number;
  endMin: number;
  placeOfService: PlaceOfService;
  maxClients: number;
  minStaff: number;
  roomResourceId: string;
  staffProviderIds: string[];
}

const DAYS: DayOfWeek[] = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

function StaffMultiSelect({
  providers,
  selected,
  onChange,
}: {
  providers: { id: string; label: string }[];
  selected: string[];
  onChange: (ids: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filtered = providers.filter((p) =>
    p.label.toLowerCase().includes(search.toLowerCase()),
  );

  const toggle = (id: string) =>
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  const selectedProviders = providers.filter((p) => selected.includes(p.id));
  const pills = selectedProviders.slice(0, 2);
  const extra = selectedProviders.length - 2;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        className="input flex min-h-[38px] w-full items-center gap-1.5 text-left"
        onClick={() => setOpen((o) => !o)}
      >
        {selected.length === 0 ? (
          <span className="text-ink-muted text-sm">Select providers…</span>
        ) : (
          <span className="flex flex-1 flex-wrap items-center gap-1.5 overflow-hidden">
            {pills.map((p) => (
              <span
                key={p.id}
                className="inline-flex shrink-0 items-center gap-1 rounded-full bg-brand-100 px-2 py-0.5 text-xs font-medium text-brand-700"
              >
                {p.label}
                <span
                  role="button"
                  className="cursor-pointer opacity-60 hover:opacity-100"
                  onMouseDown={(e) => { e.stopPropagation(); toggle(p.id); }}
                >
                  ×
                </span>
              </span>
            ))}
            {extra > 0 && (
              <span className="shrink-0 rounded-full bg-ink-muted/10 px-2 py-0.5 text-xs font-medium text-ink-muted">
                +{extra} more
              </span>
            )}
          </span>
        )}
        <span className="ml-auto shrink-0 text-ink-muted">▾</span>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-line bg-white shadow-lg">
          <div className="border-b border-line px-2 py-2">
            <input
              autoFocus
              className="w-full rounded-md border border-line px-2 py-1.5 text-sm outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
              placeholder="Search providers…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onMouseDown={(e) => e.stopPropagation()}
            />
          </div>
          <div className="max-h-48 overflow-y-auto p-1">
            {filtered.length === 0 ? (
              <p className="px-3 py-2 text-xs text-ink-muted">No providers match</p>
            ) : (
              filtered.map((p) => {
                const checked = selected.includes(p.id);
                return (
                  <label
                    key={p.id}
                    className="flex cursor-pointer items-center gap-2.5 rounded px-2 py-1.5 text-sm hover:bg-surface-muted"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(p.id)}
                      className="h-4 w-4 accent-brand-600"
                    />
                    <span className={checked ? 'font-medium text-ink' : 'text-ink-subtle'}>
                      {p.label}
                    </span>
                  </label>
                );
              })
            )}
          </div>
          <div className="flex items-center justify-between border-t border-line px-3 py-1.5 text-xs text-ink-muted">
            <span>{selected.length} selected</span>
            {selected.length > 0 && (
              <button
                type="button"
                className="text-rose-600 hover:underline"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onChange([])}
              >
                Clear all
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const EMPTY: GroupFormState = {
  serviceType: 'PHP',
  displayName: '',
  days: ['MON', 'TUE', 'WED', 'THU', 'FRI'],
  startMin: 9 * 60,
  endMin: 11 * 60,
  placeOfService: 'OFFICE',
  maxClients: 24,
  minStaff: 1,
  roomResourceId: '',
  staffProviderIds: [],
};

export function GroupForm({
  open,
  onClose,
  editing,
}: {
  open: boolean;
  onClose: () => void;
  editing?: any | null;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState<GroupFormState>(EMPTY);

  // Active room resources for the room dropdown.
  const { data: rooms = [] } = useQuery({
    queryKey: ['rooms-active'],
    queryFn: () => api.get('/resources?type=ROOM&activeOnly=true').then((r) => r.data),
    enabled: open,
  });

  // Active providers for the staff dropdown.
  const { data: providers = [] } = useQuery({
    queryKey: ['providers-active'],
    queryFn: () =>
      api.get('/providers', { params: { activeOnly: 'true' } }).then((r) =>
        (r.data as any[]).map((p) => ({
          id: p.id,
          label: `${p.firstName} ${p.lastName}`,
        })),
      ),
    enabled: open,
  });

  useEffect(() => {
    if (editing) {
      setForm({
        serviceType: editing.serviceType ?? 'PHP',
        displayName: editing.displayName ?? '',
        days: editing.days ?? ['MON', 'TUE', 'WED', 'THU', 'FRI'],
        startMin: editing.startMin ?? 9 * 60,
        endMin: editing.endMin ?? 11 * 60,
        placeOfService: editing.placeOfService ?? 'OFFICE',
        maxClients: editing.maxClients ?? 24,
        minStaff: editing.minStaff ?? 1,
        roomResourceId: editing.roomResourceId ?? '',
        staffProviderIds: (editing.staffAssignments ?? []).map((a: any) => a.providerId),
      });
    } else {
      setForm(EMPTY);
    }
  }, [editing, open]);

  const save = useMutation({
    mutationFn: () => {
      const body: any = { ...form };
      // Send null when cleared so the server unlinks the room.
      body.roomResourceId = form.roomResourceId || null;
      if (editing) {
        return api.patch(`/group-sessions/${editing.id}`, body).then((r) => r.data);
      }
      return api.post('/group-sessions', body).then((r) => r.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['groups'] });
      qc.invalidateQueries({ queryKey: ['groups-active'] });
      qc.invalidateQueries({ queryKey: ['resources-active'] });
      toast.success(editing ? 'Group updated' : 'Group created');
      onClose();
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit group session' : 'New group session'}
      subtitle="One row per concurrent track. Add a second IOP / PHP / DT here — no code change needed."
      size="lg"
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={!form.displayName || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : editing ? 'Save changes' : 'Create group'}
          </button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField label="Display name" required>
          <input
            className="input"
            value={form.displayName}
            onChange={(e) => setForm({ ...form, displayName: e.target.value })}
            placeholder="e.g. IOP — Track A (Morning)"
          />
        </FormField>
        <FormField label="Type" required>
          <select
            className="input"
            value={form.serviceType}
            onChange={(e) => setForm({ ...form, serviceType: e.target.value })}
            disabled={!!editing}
          >
            {GroupServiceTypes.map((s) => (
              <option key={s} value={s}>
                {ServiceLabels[s]}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Start time" required>
          <input
            type="time"
            className="input"
            value={toHHMM(form.startMin)}
            onChange={(e) => setForm({ ...form, startMin: fromHHMM(e.target.value) ?? form.startMin })}
          />
        </FormField>
        <FormField label="End time" required>
          <input
            type="time"
            className="input"
            value={toHHMM(form.endMin)}
            onChange={(e) => setForm({ ...form, endMin: fromHHMM(e.target.value) ?? form.endMin })}
          />
        </FormField>
        <FormField label="Place of service" required>
          <select
            className="input"
            value={form.placeOfService}
            onChange={(e) => setForm({ ...form, placeOfService: e.target.value as PlaceOfService })}
          >
            {PlacesOfService.map((p) => (
              <option key={p} value={p}>
                {PlaceLabels[p]}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Room" hint="Link to a conference room for display on the schedule.">
          <select
            className="input"
            value={form.roomResourceId}
            onChange={(e) => setForm({ ...form, roomResourceId: e.target.value })}
          >
            <option value="">— No room —</option>
            {(rooms as any[]).map((r) => (
              <option key={r.id} value={r.id}>
                {r.displayName}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Providers (staff)" hint="Providers who facilitate this group." className="sm:col-span-2">
          <StaffMultiSelect
            providers={providers as any[]}
            selected={form.staffProviderIds}
            onChange={(ids) => setForm({ ...form, staffProviderIds: ids })}
          />
        </FormField>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Max clients" required>
            <input
              type="number"
              className="input"
              min={1}
              max={200}
              value={form.maxClients}
              onChange={(e) => setForm({ ...form, maxClients: Number(e.target.value) })}
            />
          </FormField>
          <FormField label="Min staff">
            <input
              type="number"
              className="input"
              min={0}
              max={10}
              value={form.minStaff}
              onChange={(e) => setForm({ ...form, minStaff: Number(e.target.value) })}
            />
          </FormField>
        </div>
        <FormField label="Days" required>
          <div className="flex flex-wrap gap-1.5">
            {DAYS.map((d) => {
              const on = form.days.includes(d);
              return (
                <button
                  key={d}
                  type="button"
                  onClick={() =>
                    setForm({
                      ...form,
                      days: on ? form.days.filter((x) => x !== d) : [...form.days, d],
                    })
                  }
                  className={
                    on
                      ? 'rounded-md bg-brand-600 px-2.5 py-1 text-2xs font-semibold uppercase text-white'
                      : 'rounded-md bg-surface-muted px-2.5 py-1 text-2xs font-semibold uppercase text-ink-muted hover:bg-line'
                  }
                >
                  {d}
                </button>
              );
            })}
          </div>
        </FormField>
      </div>
    </Modal>
  );
}

function toHHMM(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}
function fromHHMM(v: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(v.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}
